import { QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { API_ROUTES } from "definitions/constants/routeConstants";
import { generatePath } from "react-router";
import apiClient from "services/ApiClient";
import { describeNetworkingError } from "services/ApiClient/describeNetworkingError";
import { useToast } from "@chakra-ui/react";
import { apiTaskTemplateById } from "helpers/url/api/v2";
import { TaskTemplate, TaskTemplateId } from "./models";
import { TemplateReminder } from "./models/templateReminders";

import { sharedTemplateKeys } from "../SharedTemplate/hooks";
import { toCreateTaskTemplatePayload, toUpdateTaskTemplatePayload } from "./helpers";
import { TaskTemplateSchema } from "./schema";

export const taskTemplateKeys = {
  all: ["taskTemplates"] as const,
  lists: () => [...taskTemplateKeys.all, "list"] as const,
  list: (filters: string) => [...taskTemplateKeys.lists(), { filters }] as const,
  details: () => [...taskTemplateKeys.all, "detail"] as const,
  detail: (id: TaskTemplateId) => [...taskTemplateKeys.details(), id] as const,
  reminders: () => [...taskTemplateKeys.all, "reminders"] as const,
  remindersByTaskTemplateId: (id: TaskTemplateId) => [...taskTemplateKeys.reminders(), id] as const,
};

export function setNewTaskTemplateInQueryClient(queryClient: QueryClient, newTaskTemplate: TaskTemplate) {
  queryClient.setQueryData(taskTemplateKeys.detail(newTaskTemplate.id), (oldData: TaskTemplate | undefined) => {
    if (!oldData) {
      return newTaskTemplate;
    }
    return {
      ...oldData,
      ...newTaskTemplate,
    };
  });
}

export const useTaskTemplateDetailQuery = (id: TaskTemplateId | undefined, options = {}) => useQuery<TaskTemplate | null>({
  queryKey: taskTemplateKeys.detail(id as string),
  queryFn: async () => apiClient.get(apiTaskTemplateById(id as string)),
  staleTime: 1000 * 60 * 3, // 3 minutes
  enabled: !!id && ((options as { enabled?: boolean }).enabled ?? true),
  ...options,
});

export const useTaskTemplateListQuery = (queryString: string, options = {}) => {
  const query = queryString ? `?${queryString}` : "";
  return useQuery<TaskTemplate[]>({
    queryKey: queryString ? taskTemplateKeys.list(queryString) : taskTemplateKeys.lists(),
    queryFn: async () => apiClient.get(`${generatePath(API_ROUTES.taskTemplates.base)}${query}`),
    staleTime: 1000 * 60 * 3, // 3 minutes
    ...options,
  });
};

function useTaskTemplateCreateRequest(mutationFn: (data: TaskTemplateSchema) => Promise<TaskTemplate>) {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn,
    onSuccess: (res) => {
      setNewTaskTemplateInQueryClient(queryClient, res);
      queryClient.invalidateQueries({ queryKey: taskTemplateKeys.reminders() });
      queryClient.invalidateQueries({ queryKey: sharedTemplateKeys.all });
      queryClient.invalidateQueries({ queryKey: taskTemplateKeys.all });
    },
    onError: (e) => {
      console.log(e);
      toast({
        title: "Error",
        description: describeNetworkingError(e),
        status: "error",
      });
    },
  });
}

function useTaskTemplateUpdateRequest(mutationFn: (data: Partial<TaskTemplateSchema>) => Promise<TaskTemplate>) {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn,
    onSuccess: (res) => {
      setNewTaskTemplateInQueryClient(queryClient, res);
      queryClient.invalidateQueries({ queryKey: taskTemplateKeys.reminders() });
      queryClient.invalidateQueries({ queryKey: sharedTemplateKeys.all });
      queryClient.invalidateQueries({ queryKey: taskTemplateKeys.all });
    },
    onError: (e) => {
      console.log(e);
      toast({
        title: "Error",
        description: describeNetworkingError(e),
        status: "error",
      });
    },
  });
}

export function useUpdateTaskTemplate(taskTemplateId: TaskTemplateId) {
  return useTaskTemplateUpdateRequest(
    async (formData: Partial<TaskTemplateSchema>): Promise<TaskTemplate> => {
      const payload = toUpdateTaskTemplatePayload(formData);
      return apiClient.put<TaskTemplate>(apiTaskTemplateById(taskTemplateId), { taskTemplate: payload });
    },
  );
}

export function useCreateTaskTemplate() {
  return useTaskTemplateCreateRequest(
    async (formData: TaskTemplateSchema): Promise<TaskTemplate> => {
      const payload = toCreateTaskTemplatePayload(formData);
      return apiClient.post<TaskTemplate>(API_ROUTES.taskTemplates.base, { taskTemplate: payload });
    },
  );
}

export const useListTemplateRemindersByTaskTemplateId = (taskTemplateId: TaskTemplateId, options = {}) => useQuery<TemplateReminder[]>({
  queryKey: taskTemplateKeys.remindersByTaskTemplateId(taskTemplateId),
  queryFn: async () => apiClient.get(`${apiTaskTemplateById(taskTemplateId)}/reminders`),
  staleTime: 1000 * 60 * 3, // 3 minutes
  ...options,
  enabled: !!taskTemplateId && ((options as { enabled?: boolean }).enabled ?? true),
});
